import React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useMessageContext } from '../Context/useMessageContext';
import { PushMessageIcon } from './PushMessageModal';

const ACTION_SLOT_WIDTH = 40;
const ROW_HEIGHT = 40;
const SOLID_BUTTON_SIZE = 34;

const compactIconButtonSx = {
    width: ACTION_SLOT_WIDTH,
    height: ACTION_SLOT_WIDTH,
    padding: 0,
    flexShrink: 0,
};

/** Filled circular icon button — Fab-like, without elevation. */
const solidPrimaryIconButtonSx = {
    width: SOLID_BUTTON_SIZE,
    height: SOLID_BUTTON_SIZE,
    padding: 0,
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: 'primary.main',
    color: 'secondary.main',
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: 'primary.dark',
        boxShadow: 'none',
    },
    '&:active': {
        boxShadow: 'none',
    },
    '&.Mui-disabled': {
        backgroundColor: 'action.disabledBackground',
        color: 'action.disabled',
        boxShadow: 'none',
    },
};

interface AcknowledgedMessageStripProps {
    /** Optional content pinned to the left of the row (e.g. pace display). */
    leadingContent?: React.ReactNode;
    /** Optional control pinned to the right of the row (e.g. refresh). */
    trailingAction?: React.ReactNode;
}

/** Row below the app header showing pace info on the left and push message icons in the center. */
export const AcknowledgedMessageStrip = ({ leadingContent, trailingAction }: AcknowledgedMessageStripProps) => {
    const { activeMessages, isAcknowledged, openReview } = useMessageContext();

    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{
                width: '100%',
                minHeight: ROW_HEIGHT,
                flexShrink: 0,
                px: 0.5,
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    width: leadingContent ? 'auto' : (trailingAction ? ACTION_SLOT_WIDTH : 0),
                    minWidth: leadingContent ? ACTION_SLOT_WIDTH : 0,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {leadingContent ?? null}
            </Box>
            <Stack
                direction="row"
                spacing={0.5}
                justifyContent="center"
                alignItems="center"
                sx={{ flex: 1, minWidth: 0, minHeight: ROW_HEIGHT }}
            >
                {activeMessages.length === 0 ? (
                    <Typography
                        variant="subtitle2"
                        sx={{
                            color: 'primary.main',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            px: 0.5,
                        }}
                    >
                        For Every Moment. For Every Golfer
                    </Typography>
                ) : activeMessages.map((msg) => {
                    const acked = isAcknowledged(msg.pushedAt);
                    return (
                        <IconButton
                            key={msg.pushedAt}
                            size="small"
                            onClick={() => openReview(msg.pushedAt)}
                            sx={{
                                ...compactIconButtonSx,
                                color: 'rgb(184, 115, 51)',
                                filter: acked
                                    ? 'drop-shadow(0 0 4px rgba(184, 115, 51, 0.7))'
                                    : 'drop-shadow(0 0 8px rgba(184, 115, 51, 1))',
                                animation: acked ? 'none' : 'pulse-copper 1.5s ease-in-out infinite',
                                '@keyframes pulse-copper': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.65 },
                                },
                            }}
                        >
                            <PushMessageIcon icon={msg.icon} />
                        </IconButton>
                    );
                })}
            </Stack>
            <Box
                sx={{
                    width: trailingAction ? ACTION_SLOT_WIDTH : 0,
                    minHeight: ROW_HEIGHT,
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {trailingAction}
            </Box>
        </Stack>
    );
};

export { compactIconButtonSx, solidPrimaryIconButtonSx };
